import React, { useState, useEffect } from 'react';
import { X, TrendingUp } from 'lucide-react';
import api from '../services/api';
import { MRRGraphResponse } from '../types';

interface MRRGraphProps {
  isVisible: boolean;
  onClose: () => void;
}

const MRRGraph: React.FC<MRRGraphProps> = ({ isVisible, onClose }) => {
  const [graphData, setGraphData] = useState<MRRGraphResponse['data'] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<'weekly' | 'monthly' | 'yearly'>('monthly');
  const [hoveredPoint, setHoveredPoint] = useState<{ x: number; y: number; value: number; label: string; datasetLabel: string } | null>(null);

  useEffect(() => {
    if (isVisible) {
      loadGraphData();
    }
  }, [isVisible, period]);

  const loadGraphData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.getMRRGraph(period);
      setGraphData(response.data);
    } catch (error) {
      console.error('Error loading MRR graph:', error);
      setError('Failed to load MRR graph data');
      // Mock data for development
      setGraphData({
        labels: ['Week 33', 'Week 34', 'Week 35', 'Week 36'],
        datasets: [
          {
            label: 'MRR',
            data: [12000000, 12500000, 13000000, 13500000]
          }
        ],
        periodType: period
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('id-ID').format(num);
  };

  if (!isVisible) return null;

  const renderGraph = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-2 text-sm text-gray-600">Loading MRR graph...</p>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="text-red-500 mb-2">
              <TrendingUp className="h-8 w-8 mx-auto" />
            </div>
            <p className="text-red-600 mb-2">{error}</p>
            <button
              onClick={loadGraphData}
              className="text-sm text-primary-600 hover:text-primary-800 underline"
            >
              Try again
            </button>
          </div>
        </div>
      );
    }

    if (!graphData || !graphData.datasets || graphData.datasets.length === 0) {
      return (
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="text-gray-400 mb-2">
              <TrendingUp className="h-8 w-8 mx-auto" />
            </div>
            <p className="text-gray-600">No MRR data available</p>
          </div>
        </div>
      );
    }

    const width = 800;
    const height = 400;
    const margin = { top: 20, right: 60, bottom: 60, left: 80 };
    const chartWidth = width - margin.left - margin.right;
    const chartHeight = height - margin.top - margin.bottom;

    // Calculate data ranges
    const allData = graphData.datasets.flatMap(dataset => dataset.data);
    const minValue = Math.min(...allData);
    const maxValue = Math.max(...allData);
    const valueRange = maxValue - minValue;
    const padding = valueRange * 0.1;

    // Scale functions
    const xScale = (index: number) => margin.left + (index / (graphData.labels.length - 1)) * chartWidth;
    const yScale = (value: number) => margin.top + chartHeight - ((value - minValue + padding) / (valueRange + padding * 2)) * chartHeight;

    // Generate points for each dataset
    const datasetPoints = graphData.datasets.map((dataset, datasetIndex) => {
      const color = datasetIndex === 0 ? '#3B82F6' : '#10B981'; // Blue for first, green for second
      const points = dataset.data.map((value, index) => ({
        x: xScale(index),
        y: yScale(value),
        value,
        label: graphData.labels[index],
        datasetLabel: dataset.label
      }));

      return { dataset, points, color };
    });

    return (
      <div className="w-full">
        <svg width={width} height={height} className="border rounded-lg bg-white">
          {/* Grid lines */}
          {[0, 0.25, 0.5, 0.75, 1].map((ratio, index) => {
            const y = margin.top + ratio * chartHeight;
            return (
              <line
                key={index}
                x1={margin.left}
                y1={y}
                x2={margin.left + chartWidth}
                y2={y}
                stroke="#E5E7EB"
                strokeWidth="1"
              />
            );
          })}

          {/* Y-axis labels */}
          {[0, 0.25, 0.5, 0.75, 1].map((ratio, index) => {
            const value = minValue + (1 - ratio) * (valueRange + padding * 2);
            const y = margin.top + ratio * chartHeight;
            return (
              <text
                key={index}
                x={margin.left - 10}
                y={y + 4}
                textAnchor="end"
                className="text-xs fill-gray-600"
              >
                {formatCurrency(value)}
              </text>
            );
          })}

          {/* X-axis labels */}
          {graphData.labels.map((label, index) => {
            const x = xScale(index);
            return (
              <text
                key={index}
                x={x}
                y={height - margin.bottom + 20}
                textAnchor="middle"
                className="text-xs fill-gray-600"
              >
                {label}
              </text>
            );
          })}

          {/* Draw lines and points for each dataset */}
          {datasetPoints.map(({ dataset, points, color }, datasetIndex) => {
            const pathData = points.map((point, index) => 
              `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`
            ).join(' ');

            return (
              <g key={datasetIndex}>
                {/* Line */}
                <path
                  d={pathData}
                  fill="none"
                  stroke={color}
                  strokeWidth="3"
                  strokeOpacity={hoveredPoint ? (hoveredPoint.datasetLabel === dataset.label ? 1 : 0.3) : 1}
                  className="transition-all duration-200"
                />
                
                {/* Points */}
                {points.map((point, pointIndex) => (
                  <circle
                    key={pointIndex}
                    cx={point.x}
                    cy={point.y}
                    r={hoveredPoint === point ? 8 : 6}
                    fill={color}
                    stroke="white"
                    strokeWidth="2"
                    className="cursor-pointer transition-all duration-200"
                    onMouseEnter={() => setHoveredPoint(point)}
                    onMouseLeave={() => setHoveredPoint(null)}
                    style={{
                      fillOpacity: hoveredPoint === point ? 0.8 : 1
                    }}
                  />
                ))}
              </g>
            );
          })}

          {/* Tooltip */}
          {hoveredPoint && (
            <g>
              {/* Tooltip background */}
              <rect
                x={hoveredPoint.x - 70}
                y={hoveredPoint.y - 60}
                width="140"
                height="60"
                fill="rgba(0, 0, 0, 0.8)"
                rx="6"
                ry="6"
              />
              {/* Tooltip text */}
              <text
                x={hoveredPoint.x}
                y={hoveredPoint.y - 40}
                textAnchor="middle"
                className="text-xs fill-white font-medium"
              >
                {hoveredPoint.datasetLabel}
              </text>
              <text
                x={hoveredPoint.x}
                y={hoveredPoint.y - 25}
                textAnchor="middle"
                className="text-xs fill-white"
              >
                {hoveredPoint.label}
              </text>
              <text
                x={hoveredPoint.x}
                y={hoveredPoint.y - 10}
                textAnchor="middle"
                className="text-xs fill-white font-semibold"
              >
                {formatCurrency(hoveredPoint.value)}
              </text>
            </g>
          )}
        </svg>

        {/* Summary Statistics */}
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {graphData.datasets.map((dataset, index) => {
            const total = dataset.data.reduce((sum, value) => sum + value, 0);
            const average = total / dataset.data.length;
            const max = Math.max(...dataset.data);
            const min = Math.min(...dataset.data);
            const color = index === 0 ? 'blue' : 'green';

            return (
              <div key={index} className="bg-gray-50 rounded-lg p-4">
                <h4 className={`text-sm font-medium text-${color}-600 mb-3`}>
                  {dataset.label}
                </h4>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div>
                    <span className="text-gray-500">Total:</span>
                    <div className="font-semibold text-gray-900">
                      {formatCurrency(total)}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-500">Average:</span>
                    <div className="font-semibold text-gray-900">
                      {formatCurrency(average)}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-500">Max:</span>
                    <div className="font-semibold text-gray-900">
                      {formatCurrency(max)}
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-500">Min:</span>
                    <div className="font-semibold text-gray-900">
                      {formatCurrency(min)}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">MRR Trend Analysis</h2>
            <p className="text-sm text-gray-600 mt-1">
              Monthly Recurring Revenue trends over time
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value as 'weekly' | 'monthly' | 'yearly')}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
            </select>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>
        
        <div className="p-6">
          {renderGraph()}
        </div>
      </div>
    </div>
  );
};

export default MRRGraph;
